<template>
  <div class="setup">

    <template v-if="stepRef === 'intro'">
      <div class="preview-cards">
        <div class="preview-card" :class="{ 'is-up': imageFlippedRef }">
          <div class="preview-card-inner">
            <div class="preview-card-back"></div>
            <div class="preview-card-front">
              <MatchingImageCard
                :src="firstImageSrc"
                :focal="firstPair.images[0]"
                :relationship="firstPair.relationship"
                :alt="firstPair.name"
                :highlight-relationship="showKeyRef"
              />
            </div>
          </div>
        </div>
        <div class="preview-card" :class="{ 'is-up': infoFlippedRef }">
          <div class="preview-card-inner">
            <div class="preview-card-back"></div>
            <div class="preview-card-front preview-info-front">
              <div class="preview-name">{{ firstPair.name }}</div>
              <div class="preview-info-middle">
                <div class="preview-text">{{ firstPair.text }}</div>
              </div>
              <div class="preview-footer">
                <div class="rel-key" :class="{ 'rel-key-highlight': showKeyRef }">
                  <span
                    v-for="(relStep, i) in relationshipStepsRef"
                    :key="i"
                    class="rel-square"
                    :class="relStep"
                  ></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p class="relation-caption" :class="{ visible: showKeyRef }">Kent's {{ relationTextRef }}</p>
      <p class="setup-tagline">Stack up matches while learning fun facts<br>about your ancestors.</p>
      <button class="btn btn-primary btn-lg" @click="stepRef = 'teams'">Get started</button>
    </template>

    <template v-else>
      <h2 class="setup-invite">Choose team names</h2>
      <div class="setup-teams">
        <TeamColorPicker
          v-for="(team, i) in teams"
          :key="team.id"
          :color="colors[team.id]"
          :name="names[team.id]"
          :open="pickerOpenRef === team.id"
          :hint-delay="i * 180"
          @toggle="togglePicker(team.id)"
          @update:color="emit('update:color', team.id, $event)"
          @update:name="emit('update:name', team.id, $event)"
        />
      </div>
      <button class="btn btn-primary btn-lg" @click="emitStart">Start game</button>
    </template>

  </div>
</template>

<style lang="scss" scoped>
.setup {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(2rem * var(--ui-scale, 1));
  padding-top: 1rem;
}

// --- intro ---

.preview-cards {
  display: flex;
  gap: calc(1.5rem * var(--ui-scale, 1));
  // enlarged "large" layout: thicken both card faces' borders (2x the standard 1px) and scale
  // them with --ui-scale like the rest of the chrome so they stay proportional from browser to
  // fullscreen. The var is inherited by MatchingImageCard and .preview-info-front, and is the
  // same knob game play uses when flipping a card up to this size.
  --card-border-width: calc(2px * var(--ui-scale, 1));
}

.preview-card {
  width: calc(260px * var(--ui-scale, 1));
  height: calc(260px * var(--ui-scale, 1));
  perspective: calc(1200px * var(--ui-scale, 1));
  container-type: inline-size;
}

.preview-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.5s;
  transform-style: preserve-3d;
}

.preview-card.is-up .preview-card-inner {
  transform: rotateY(180deg);
}

.preview-card-back,
.preview-card-front {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  border-radius: 12px;
  overflow: hidden;
}

.preview-card-back {
  background-image: url('@/assets/playing-card-square.png');
  background-size: cover;
  background-position: center;
  filter: contrast(0.25) brightness(1.2) sepia(0.7);
}

.preview-card-front {
  transform: rotateY(180deg);
}

.preview-info-front {
  background: #fff;
  border: var(--card-border-width, 1px) solid #ccc;
  display: flex;
  flex-direction: column;
}

.preview-name {
  padding: 5cqi 5cqi 0;
  font-size: 10cqi;
  font-weight: bold;
  text-align: center;
  line-height: 1.15;
}

.preview-info-middle {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5cqi;
}

.preview-text {
  font-size: 10cqi;
  color: #888;
  text-align: center;
  line-height: 1.15;
}

.preview-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 4cqi;
}

// groups the relationship squares so the highlight box hugs just them. An outline (not a border)
// is used so the box takes no layout space and never nudges the squares when it appears.
.rel-key {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2.5cqi;
  border-radius: 0.75cqi;
  // offset + width must stay within the footer's 4cqi padding so the box isn't clipped by the
  // card's overflow:hidden (which rounds the corners and can't simply be turned off)
  outline: 2cqi solid transparent;
  outline-offset: 1.5cqi;
  transition: outline-color 0.7s ease;
}

.rel-key-highlight {
  outline-color: #dc2626;
}

.rel-square {
  width: 6cqi;
  height: 6cqi;
  border-radius: 1.5cqi;

  &.f {
    background: #73a7f0;
  }

  &.m {
    background: #f2c1d6;
  }
}

// --- relationship caption ---

// "Kent's <relationship>" -- fades in together with the relationship highlight box on the cards
.relation-caption {
  margin: calc(-1rem * var(--ui-scale, 1)) 0 0;
  text-align: center;
  font-size: calc(1.5rem * var(--ui-scale, 1));
  font-weight: bold;
  color: #dc2626; // match the relationship highlight boxes on the cards
  opacity: 0;
  transition: opacity 0.7s ease;
}

.relation-caption.visible {
  opacity: 1;
}

// --- tagline ---

.setup-tagline {
  margin: 0;
  text-align: center;
  line-height: 1.5;
  font-size: calc(1.875rem * var(--ui-scale, 1));
}

// scale the primary action buttons to match the rest of the setup chrome (1.25x the base size)
.btn-lg {
  font-size: calc(1.5625rem * var(--ui-scale, 1));
  padding: calc(0.625rem * var(--ui-scale, 1)) calc(1.25rem * var(--ui-scale, 1));
  border-radius: calc(0.625rem * var(--ui-scale, 1));
}

// --- teams ---

.setup-invite {
  margin: 0;
  font-weight: normal;
  // match the game subtitle (and the team names below)
  font-size: calc(1.75rem * var(--ui-scale, 1));
}

.setup-teams {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: calc(6rem * var(--ui-scale, 1));
}
</style>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { MatchingCardPair } from './MatchingGameDefinition'
import MatchingImageCard from './MatchingImageCard.vue'
import TeamColorPicker from '../TeamColorPicker.vue'
import * as sounds from './GameSounds'
import { getAncestorRelationText } from '@/rd/RelationPath'
import { Gender } from '@/rd/ResearchDataModel'

type SetupStep = 'intro' | 'teams'

const props = defineProps<{
  teams: { id: number }[]
  colors: string[]
  names: string[]
  firstPair: MatchingCardPair
  firstImageSrc: string
}>()

const emit = defineEmits<{
  'update:color': [teamId: number, color: string]
  'update:name': [teamId: number, name: string]
  start: []
}>()

const stepRef = ref<SetupStep>('intro')
const imageFlippedRef = ref(false)
const infoFlippedRef = ref(false)
const pickerOpenRef = ref<number | null>(null)
// once true (~1s after the info card flips up) the relationship squares get a red highlight box
// and the "Kent's <relationship>" caption fades in
const showKeyRef = ref(false)

let flipTimer1 = 0
let flipTimer2 = 0
let keyTimer = 0

onMounted(() => {
  flipTimer1 = window.setTimeout(() => {
    sounds.cardReveal()
    imageFlippedRef.value = true
  }, 1000)
  flipTimer2 = window.setTimeout(() => {
    sounds.cardReveal()
    infoFlippedRef.value = true
  }, 1500)
  // a beat after the second card flips, highlight the relationship key and reveal the caption
  keyTimer = window.setTimeout(() => { showKeyRef.value = true }, 3500)
})

onUnmounted(() => {
  window.clearTimeout(flipTimer1)
  window.clearTimeout(flipTimer2)
  window.clearTimeout(keyTimer)
})

const relationshipStepsRef = computed(() =>
  [...(props.firstPair.relationship || '')].map(s => s === 'F' ? 'f' : 'm'))

// the ancestor relationship word (e.g. "grandfather"), built from the F/M path. The first square is
// the root person (Kent) in the root couple, so generations-up = length - 1 (e.g. "FF" = Kent's
// father, "FFF" = Kent's grandfather); the final square gives the ancestor's gender. Reuses the
// shared builder.
const relationTextRef = computed(() => {
  const rel = props.firstPair.relationship || ''
  const generations = rel.length - 1
  if (generations < 1) return ''
  const gender = rel.at(-1) === 'F' ? Gender.Male : Gender.Female
  return getAncestorRelationText(generations, gender)
})

function togglePicker(teamId: number) {
  pickerOpenRef.value = pickerOpenRef.value === teamId ? null : teamId
}

function emitStart() {
  emit('start')
}
</script>
